import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Typography,
  Space,
  Switch,
  Card,
  Alert,
  Modal,
  Tag,
  message,
} from 'antd';
import Table from 'components/Common/Table';
import {
  ArrowLeftOutlined,
  SendOutlined,
  BellOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';

const { Title, Text } = Typography;
const { TextArea } = Input;

const NOTIFICATION_OPTIONS = [
  { key: 'signed', label: 'When document is signed', description: 'Notify when a recipient signs' },
  { key: 'request', label: 'Signature request sent', description: 'Notify when request is sent' },
  { key: 'pending', label: 'Signature pending', description: 'Reminder for pending signatures' },
  { key: 'completed', label: 'All signatures complete', description: 'Notify when everyone has signed' },
  { key: 'declined', label: 'Signature declined', description: 'Notify if someone declines' },
];

const StepDistribution = ({
  formData,
  onNext,
  onBack,
  onSubmit,
  documentId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailResults, setEmailResults] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [notifications, setNotifications] = useState({
    signed: true,
    request: true,
    pending: true,
    completed: true,
    declined: true,
  });

  useEffect(() => {
    if (formData) {
      form.setFieldsValue({
        subject: formData.email_subject || `Please sign: ${formData.title || formData.name || 'Document'}`,
        message: formData.email_message || '',
      });

      if (formData.notifications) {
        setNotifications(formData.notifications);
      }
    }
  }, [formData, form]);

  const handleNotificationChange = (key, value) => {
    setNotifications((prev) => ({ ...prev, [key]: value }));
  };

  const handleFinish = async (values) => {
    setLoading(true);
    setSendingEmails(true);

    try {
      const distributionData = {
        subject: values.subject,
        message: values.message,
        notifications,
        step: 4,
      };

      // First save the workflow data
      await onSubmit(4, distributionData);

      // Then send emails to all recipients
      try {
        const result = await actions.sendDocumentWorkflow(documentId, distributionData);

        setEmailResults(result);
        setShowResultsModal(true);

        if (result?.status === 'success') {
          message.success(`Document sent to ${result.results?.length || 0} recipients`);
        } else if (result?.status === 'partial') {
          message.warning(result.message || 'Some emails failed to send');
        }
      } catch (emailError) {
        console.error('Email sending error:', emailError);
        message.error('Document saved but failed to send emails. You can resend later.');
      }
    } catch (error) {
      console.error('Distribution error:', error);
      message.error('Failed to save distribution settings');
    } finally {
      setLoading(false);
      setSendingEmails(false);
    }
  };

  // Handle resend for failed emails
  const handleResendEmail = async (emailLogId) => {
    try {
      await actions.resendDocumentEmail(documentId, emailLogId);
      // Refresh email results after resend
      const logs = await actions.getDocumentEmailLogs(documentId);
      if (logs) {
        setEmailResults((prev) => ({
          ...prev,
          results: logs,
        }));
      }
    } catch (error) {
      console.error('Resend error:', error);
    }
  };


  const totalRecipients = (formData.recipients || []).length;

  // Get role description for display
  const getRoleDescription = (role) => {
    const descriptions = {
      SIGNER: 'Will receive signing link',
      VIEWER: 'Will receive view-only link',
      APPROVER: 'Will receive approval link',
      CC: 'Will receive read-only copy',
    };
    return descriptions[role] || 'Will be notified';
  };

  // Results table columns
  const resultsColumns = [
    {
      title: 'Recipient',
      dataIndex: 'recipient',
      key: 'recipient',
      render: (_, record) => record.recipient_name || record.recipient,
    },
    {
      title: 'Email',
      dataIndex: 'recipient_email',
      key: 'email',
      render: (_, record) => record.recipient_email || record.email,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => <Tag>{role}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'success',
      key: 'status',
      render: (success, record) => (
        success !== false && record.status !== 'failed' ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>Sent</Tag>
        ) : (
          <Tag color="red" icon={<CloseCircleOutlined />}>Failed</Tag>
        )
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        record.success === false || record.status === 'failed' ? (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => handleResendEmail(record.id || record.log_id)}
          >
            Resend
          </Button>
        ) : null
      ),
    },
  ];

  return (
    <div>
      <Title level={4}>Distribution Settings</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Configure how the document will be sent to recipients.
      </Text>

      {/* Summary Alert */}
      <Alert
        message={`Ready to send to ${totalRecipients} recipient(s)`}
        description={
          <div>
            {(formData.recipients || []).map((r) => (
              <div key={r.id || r.key} style={{ marginBottom: 4 }}>
                <Text strong>{r.name}</Text>
                <Text type="secondary"> ({r.email})</Text>
                <Text> - </Text>
                <Tag color={
                  r.recipient_role === 'SIGNER' ? 'blue' :
                  r.recipient_role === 'APPROVER' ? 'green' :
                  r.recipient_role === 'VIEWER' ? 'orange' : 'default'
                }>
                  {r.recipient_role}
                </Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {getRoleDescription(r.recipient_role)}
                </Text>
              </div>
            ))}
          </div>
        }
        type="info"
        showIcon
        icon={<MailOutlined />}
        style={{ marginBottom: 24 }}
      />

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {/* Email Configuration */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Title level={5} style={{ marginBottom: 16 }}>
            <MailOutlined style={{ marginRight: 8 }} />
            Email Configuration
          </Title>

          <Form.Item
            name="subject"
            label="Email Subject"
            rules={[{ required: true, message: 'Please enter email subject' }]}
          >
            <Input placeholder="Enter email subject line" />
          </Form.Item>

          <Form.Item name="message" label="Custom Message (Optional)">
            <TextArea
              rows={4}
              placeholder="Add a personal message to include in the email"
            />
          </Form.Item>
        </Card>

        {/* Role-based Link Information */}
        <Alert
          message="Secure Links"
          description={
            <div>
              <Text>Each recipient will receive a unique, secure link based on their role:</Text>
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li><strong>Signers</strong>: Can view and sign the document</li>
                <li><strong>Approvers</strong>: Can approve or reject (after all signatures)</li>
                <li><strong>Viewers</strong>: Can view and download only</li>
                <li><strong>CC</strong>: Receives a read-only copy</li>
              </ul>
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Links expire after 12 hours for security. You can resend if needed.
              </Text>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* Notification Preferences */}
        <Card size="small" style={{ marginBottom: 24 }}>
          <Title level={5} style={{ marginBottom: 16 }}>
            <BellOutlined style={{ marginRight: 8 }} />
            Notification Preferences
          </Title>

          {NOTIFICATION_OPTIONS.map((option) => (
            <div
              key={option.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <div>
                <Text>{option.label}</Text>
                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                  {option.description}
                </Text>
              </div>
              <Switch
                checked={notifications[option.key]}
                onChange={(checked) => handleNotificationChange(option.key, checked)}
              />
            </div>
          ))}
        </Card>

        {/* Navigation Buttons */}
        <Form.Item>
          <Space>
            <Button onClick={onBack} icon={<ArrowLeftOutlined />} size="large">
              Back
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading || sendingEmails}
              icon={<SendOutlined />}
              size="large"
            >
              {sendingEmails ? 'Sending Emails...' : 'Send for Signatures'}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Email Results Modal */}
      <Modal
        title="Email Delivery Status"
        open={showResultsModal}
        onCancel={() => setShowResultsModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowResultsModal(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {emailResults && (
          <>
            <Alert
              message={
                emailResults.status === 'success'
                  ? 'All emails sent successfully!'
                  : emailResults.status === 'partial'
                  ? 'Some emails failed to send'
                  : 'Document sent'
              }
              type={emailResults.status === 'success' ? 'success' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              dataSource={emailResults.results || []}
              columns={resultsColumns}
              rowKey={(record) => record.id || record.recipient_email || record.email}
              pagination={false}
              size="small"
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default StepDistribution;
